import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bike, Receipt, ShoppingBag, StickyNote, UtensilsCrossed } from 'lucide-react';
import type { DirtyForm } from '../../hooks/useDirtyForm';
import { ChangedDot, SheetSection } from '../settings/sheet';
import { SettingsSwitch } from '../settings/storeSettingsForm';
import { PlatformKey, PLATFORM_KEYS, PrintingPrinter, PrintingZone } from './printingApi';
import {
  InactiveTarget, OrderTargetKey, OrderTargetsInfo, PageForm, ROUTING_KEYS, SWITCH_KEYS,
  receiptPrinters, targetIds, ticketPrinters,
} from './printingModel';
import { Notice, SELECT_CLS } from './printingUi';

/**
 * «Πού βγαίνει κάθε χαρτί» (απόφαση ιδιοκτήτη 25/10/2026: «οι τεχνικοί δεν ξέρουν τι πρέπει να κάνουν
 * πού»): πάνω οι δύο ΚΥΡΙΟΙ εκτυπωτές (αποδείξεων / δελτίων), από κάτω μία κάρτα ανά είδος παραγγελίας —
 * Take Away, Delivery & πλατφόρμες, Τραπέζια — με δύο γραμμές: πού βγαίνει η απόδειξη και πού το δελτίο.
 * «Όπως ο κύριος» = στον κύριο εκτυπωτή. Οι περιοχές τραπεζιών (ίδια ρύθμιση με την κάτοψη) αφορούν ΜΟΝΟ
 * τις αποδείξεις («Μόνο αποδείξεις»). Τα δελτία ανά κατηγορία είναι δική τους ενότητα, πιο κάτω.
 * Τίποτα δεν φεύγει μόνο του: η μπάρα αλλαγών της σελίδας σώζει όλη την ενότητα.
 */

interface Props {
  printers: PrintingPrinter[];
  zones: PrintingZone[];
  form: DirtyForm<PageForm>;
  takeaway: boolean;
  dineIn: boolean;
  /** Από τον server: αν ξέρει τους στόχους ανά είδος παραγγελίας (274) και ποιοι δεν ισχύουν πια. */
  orderTargets: OrderTargetsInfo;
}

interface Option { value: string; label: string }
/** Ο ρόλος που θέλει ο στόχος: τα δελτία σε εκτυπωτή δελτίων, τα παραστατικά σε εκτυπωτή αποδείξεων. */
type Role = 'tickets' | 'receipts';

const PLATFORM_LABEL: Record<PlatformKey, string> = { efood: 'efood', wolt: 'Wolt', box: 'BOX' };
const OTHER_SECTION_KEYS: Array<keyof PageForm> = ['rules', 'byCategory', 'ticket_bold'];

/** Μία κάρτα «είδους παραγγελίας». */
const Card: React.FC<{ icon: React.ElementType; title: string; testId: string; children: React.ReactNode }> = ({ icon: Icon, title, testId, children }) => (
  <div className="rounded-2xl border border-line bg-surface-1 px-4 sm:px-5 py-1" data-testid={testId}>
    <h3 className="flex items-center gap-2 pt-3.5 pb-1 text-sm font-bold text-t1">
      <Icon className="w-4 h-4 text-brand shrink-0" aria-hidden="true" />{title}
    </h3>
    <div className="divide-y divide-line">{children}</div>
  </div>
);

/** Γραμμή κάρτας: «Απόδειξη» / «Δελτίο» αριστερά, τι γίνεται δεξιά. */
const PaperRow: React.FC<{ kind: 'receipt' | 'ticket'; label: string; hint?: React.ReactNode; changed?: boolean; children: React.ReactNode }> = ({
  kind, label, hint, changed = false, children,
}) => {
  const Icon = kind === 'receipt' ? Receipt : StickyNote;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-2 py-3.5">
      <div className="flex items-start gap-2 min-w-0 pt-2">
        <Icon className="w-4 h-4 mt-0.5 shrink-0 text-t3" aria-hidden="true" />
        <span className="text-sm font-semibold text-t1">{label}{changed && <ChangedDot />}</span>
      </div>
      <div className="min-w-0 space-y-2">
        {children}
        {hint && <p className="text-xs leading-[18px] text-t3">{hint}</p>}
      </div>
    </div>
  );
};

const RoutingSection: React.FC<Props> = ({ printers, zones, form, takeaway, dineIn, orderTargets }) => {
  const { t } = useTranslation(['printing']);
  const { values: v, saved, setField, changedKeys } = form;
  const tickets = ticketPrinters(printers);
  const receipts = receiptPrinters(printers);
  const roleIds: Record<Role, Set<number>> = {
    tickets: new Set(tickets.map(p => p.id)),
    receipts: new Set(receipts.map(p => p.id)),
  };
  // Οι στόχοι του 274 μόνο με server που τους ξέρει (αλλιώς η επιλογή θα χανόταν σιωπηλά).
  const shows: Record<OrderTargetKey, boolean> = {
    takeaway: orderTargets.supported && takeaway,
    dinein: orderTargets.supported && dineIn,
    receiptsDelivery: orderTargets.supported,
  };

  const name = (id: number) => printers.find(p => p.id === id)?.name || `#${id}`;
  const names = (ids: number[]) => ids.map(name).join(' + ');
  /** Εκτυπωτής που δεν έχει πια τον ρόλο αλλά είναι επιλεγμένος: φαίνεται, για να μην «εξαφανίζεται» η τιμή. */
  const optionFor = (value: string, role: Role): Option => {
    const ids = targetIds(value);
    const label = names(ids);
    if (ids.every(id => roleIds[role].has(id))) return { value, label };
    return {
      value,
      label: role === 'tickets' ? t('printing:routing.notTickets', { name: label }) : t('printing:routing.notReceipts', { name: label }),
    };
  };
  const optionsWith = (role: Role, ...current: string[]): Option[] => {
    const out: Option[] = (role === 'tickets' ? tickets : receipts).map(p => ({ value: String(p.id), label: p.name }));
    current.forEach(value => {
      if (value && !out.some(o => o.value === value)) out.push(optionFor(value, role));
    });
    return out;
  };

  const changed = (...keys: Array<keyof PageForm>) => keys.some(k => changedKeys.includes(k));
  // Οι κανόνες κατηγοριών και τα «Έντονα» έχουν δικές τους ενότητες (και δική τους τελεία).
  const sectionDirty = changedKeys.some(k => (ROUTING_KEYS.includes(k) || SWITCH_KEYS.includes(k)) && !OTHER_SECTION_KEYS.includes(k));
  const activePlatforms = PLATFORM_KEYS.filter(k => v[k] !== null);

  const mainReceiptName = v.mainReceipt ? names(targetIds(v.mainReceipt)) : null;
  const mainTicketName = v.unrouted ? names(targetIds(v.unrouted)) : null;
  const asMainReceipt = mainReceiptName ? t('printing:routing.asMain', { name: mainReceiptName }) : t('printing:routing.asMainNone');
  // Χωρίς δικό του εκτυπωτή το δελτίο Take Away/τραπεζιού ακολουθεί τις κατηγορίες — ή, χωρίς αυτές, τον κύριο.
  const asTicketDefault = v.byCategory
    ? t('printing:routing.byCategoryOption')
    : mainTicketName ? t('printing:routing.asMain', { name: mainTicketName }) : t('printing:routing.asMainNoTicket');

  // Αποθηκευμένος στόχος που ο server δεν εφαρμόζει (ο εκτυπωτής έχασε τον ρόλο): η φόρμα δείχνει ήδη
  // την προεπιλογή, εδώ λέμε τι έγινε — ώσπου να διαλέξει άλλον ή να σώσει.
  const inactive = orderTargets.inactive.filter(w => shows[w.key] && v[w.key] === '');
  const inactiveText = (w: InactiveTarget) => {
    if (w.key === 'takeaway') return t('printing:routing.inactiveTakeaway', { name: name(w.printerId) });
    if (w.key === 'dinein') return t('printing:routing.inactiveDinein', { name: name(w.printerId) });
    return t('printing:routing.inactiveReceiptsDelivery', { name: name(w.printerId) });
  };

  const select = (id: string, value: string, onChange: (next: string) => void, role: Role, empty: string | null,
    current: string[], ariaLabel?: string) => (
    <select id={id} value={value} onChange={e => onChange(e.target.value)} aria-label={ariaLabel} className={SELECT_CLS}>
      {empty !== null && <option value="">{empty}</option>}
      {optionsWith(role, ...current).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const switchControl = (key: 'takeaway_ticket' | 'dinein_kitchen_ticket' | PlatformKey, label: string) => {
    const labelId = `printing-switch-${key}`;
    return (
      <div className="flex items-center gap-3 min-h-[44px]">
        <SettingsSwitch checked={v[key] === true} onChange={next => setField(key, next)} labelId={labelId} />
        <span id={labelId} className="text-sm text-t1">{label}{changed(key) && <ChangedDot />}</span>
      </div>
    );
  };

  /** Δελτίο Take Away / τραπεζιού: διακόπτης «βγαίνει δελτίο» και, ανοιχτός, πού. */
  const ticketControls = (sw: 'takeaway_ticket' | 'dinein_kitchen_ticket', target: 'takeaway' | 'dinein', own: boolean) => (
    <>
      {switchControl(sw, t(sw === 'takeaway_ticket' ? 'printing:routing.takeawayTicketOn' : 'printing:routing.dineinTicketOn'))}
      {v[sw] && own && select(`printing-${target}`, v[target], next => setField(target, next), 'tickets', asTicketDefault,
        [v[target], saved[target]], t(target === 'takeaway' ? 'printing:routing.takeawayTicketAria' : 'printing:routing.dineinTicketAria'))}
      {v[sw] && !own && <p className="text-sm text-t2">{asTicketDefault}</p>}
    </>
  );

  return (
    <SheetSection id="printing-routing" title={t('printing:routing.title')} hint={t('printing:routing.hint')}
      dirty={sectionDirty} testId="routing-section">
      <div className="space-y-2.5 empty:hidden mb-5" data-testid="routing-warnings">
        {tickets.length === 0 && <Notice tone="danger" testId="warn-no-tickets">{t('printing:routing.warnNoTicketPrinter')}</Notice>}
        {receipts.length === 0 && <Notice tone="danger" testId="warn-no-receipts">{t('printing:routing.warnNoReceiptPrinter')}</Notice>}
        {tickets.length > 0 && v.unrouted === '' && !v.byCategory && (
          <Notice testId="warn-unrouted">{t('printing:routing.warnNoMainTicket')}</Notice>
        )}
        {tickets.length > 0 && v.delivery === '' && <Notice testId="warn-delivery">{t('printing:routing.warnDelivery')}</Notice>}
        {inactive.map(w => <Notice key={w.key} testId={`warn-inactive-${w.key}`}>{inactiveText(w)}</Notice>)}
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-line bg-surface-2 px-4 sm:px-5 py-4" data-testid="main-printers">
          <h3 className="text-sm font-bold text-t1 mb-3">{t('printing:routing.mainTitle')}</h3>
          <div className="xs-grid2">
            <div className="xs-field">
              <label htmlFor="printing-main-receipt" className="xs-label">
                {t('printing:routing.mainReceipt')}{changed('mainReceipt') && <ChangedDot />}
              </label>
              {select('printing-main-receipt', v.mainReceipt, next => setField('mainReceipt', next), 'receipts',
                receipts.length === 0 ? t('printing:routing.noReceiptPrinter') : null, [v.mainReceipt, saved.mainReceipt])}
              <p id="printing-main-receipt-hint" className="xs-field-hint">{t('printing:routing.mainReceiptHint')}</p>
            </div>
            <div className="xs-field">
              <label htmlFor="printing-unrouted" className="xs-label">
                {t('printing:routing.mainTicket')}{changed('unrouted') && <ChangedDot />}
              </label>
              {select('printing-unrouted', v.unrouted, next => setField('unrouted', next), 'tickets', t('printing:routing.none'),
                [v.unrouted, saved.unrouted])}
              <p id="printing-unrouted-hint" className="xs-field-hint">{t('printing:routing.mainTicketHint')}</p>
            </div>
          </div>
        </div>

        {takeaway && (
          <Card icon={ShoppingBag} title={t('printing:routing.cardTakeaway')} testId="card-takeaway">
            <PaperRow kind="receipt" label={t('printing:routing.receipt')}>
              <p className="text-sm text-t2 pt-2" data-testid="takeaway-receipt">{asMainReceipt}</p>
            </PaperRow>
            <PaperRow kind="ticket" label={t('printing:routing.ticket')} changed={changed('takeaway_ticket', 'takeaway')}
              hint={v.takeaway_ticket && v.takeaway !== '' ? t('printing:routing.wholeOrderHint') : undefined}>
              {ticketControls('takeaway_ticket', 'takeaway', shows.takeaway)}
            </PaperRow>
          </Card>
        )}

        <Card icon={Bike} title={t('printing:routing.cardDelivery')} testId="card-delivery">
          <PaperRow kind="receipt" label={t('printing:routing.receiptDelivery')} changed={changed('receiptsDelivery')}
            hint={t('printing:routing.receiptsDeliveryHint')}>
            {shows.receiptsDelivery
              ? select('printing-receipts-delivery', v.receiptsDelivery, next => setField('receiptsDelivery', next), 'receipts', asMainReceipt,
                [v.receiptsDelivery, saved.receiptsDelivery], t('printing:routing.receiptDeliveryAria'))
              : <p className="text-sm text-t2 pt-2">{asMainReceipt}</p>}
          </PaperRow>
          <PaperRow kind="ticket" label={t('printing:routing.ticket')} changed={changed('delivery', 'efood', 'wolt', 'box')}
            hint={t('printing:routing.deliveryHint')}>
            {select('printing-delivery', v.delivery, next => setField('delivery', next), 'tickets', t('printing:routing.none'),
              [v.delivery, saved.delivery], t('printing:routing.deliveryAria'))}
            {activePlatforms.length > 0 && (
              <div className="pt-1" data-testid="platform-switches">
                <p className="text-xs font-semibold text-t3 mb-1">{t('printing:routing.platformsAuto')}</p>
                <div className="flex flex-wrap gap-x-6">
                  {activePlatforms.map(k => <React.Fragment key={k}>{switchControl(k, PLATFORM_LABEL[k])}</React.Fragment>)}
                </div>
              </div>
            )}
          </PaperRow>
        </Card>

        {dineIn && (
          <Card icon={UtensilsCrossed} title={t('printing:routing.cardDinein')} testId="card-dinein">
            <PaperRow kind="receipt" label={t('printing:routing.receiptDinein')} changed={changed('zones')}
              hint={zones.length > 0 ? t('printing:routing.zonesHint') : t('printing:routing.noZonesHint')}>
              {zones.length === 0 ? (
                <p className="text-sm text-t2 pt-2" data-testid="dinein-receipt">{asMainReceipt}</p>
              ) : (
                <div className="grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] items-center gap-x-3 gap-y-2" data-testid="zone-printers">
                  {zones.map(z => {
                    const key = String(z.id);
                    const value = v.zones[key] || '';
                    return (
                      <React.Fragment key={z.id}>
                        <label htmlFor={`printing-zone-${z.id}`} className="min-w-0 break-words text-sm font-medium text-t2">
                          {z.name}{value !== (saved.zones[key] || '') && <ChangedDot />}
                        </label>
                        {select(`printing-zone-${z.id}`, value, next => form.setValues(prev => ({ ...prev, zones: { ...prev.zones, [key]: next } })),
                          'receipts', asMainReceipt, [value, saved.zones[key] || ''])}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </PaperRow>
            <PaperRow kind="ticket" label={t('printing:routing.ticketKitchen')} changed={changed('dinein_kitchen_ticket', 'dinein')}
              hint={v.dinein_kitchen_ticket && v.dinein !== '' ? t('printing:routing.wholeOrderHint') : undefined}>
              {ticketControls('dinein_kitchen_ticket', 'dinein', shows.dinein)}
            </PaperRow>
          </Card>
        )}
      </div>
    </SheetSection>
  );
};

export default RoutingSection;
